import { FileText, Search, FileEdit, Bell, type LucideIcon } from 'lucide-react';
import type { ChatMode } from '../chat/types';
import type { LoadedChatType } from '../chat/useChat';

/**
 * Map a backend notification `feature` to an in-pane open target. Ported (and
 * adapted) from the web app's `mapFeatureToRoute` — instead of a web route we
 * return what the add-in needs to load the chat thread in the pane.
 *
 * Only draft/edit and research notifications carry a chat thread we can open
 * (their `feature_id` is the chat id). Everything else (doc-processing,
 * studychat, etc.) has no in-pane destination, so it's display-only.
 */
export type OpenTarget =
  | { kind: 'draft' | 'research'; chatId: string; mode: ChatMode; chatType: LoadedChatType }
  | { kind: 'none' };

const DRAFT_FEATURES = new Set([
  'draft_generation',
  'draft_generated',
  'full_edit',
  'template_generation',
  'questions_generation',
]);

const RESEARCH_FEATURES = new Set(['deep_research', 'quick_research', 'llm_direct']);

export function featureToTarget(feature?: string, featureId?: string): OpenTarget {
  if (!feature || !featureId) return { kind: 'none' };
  if (DRAFT_FEATURES.has(feature)) {
    return { kind: 'draft', chatId: featureId, mode: 'edit', chatType: 'draft' };
  }
  if (RESEARCH_FEATURES.has(feature)) {
    return { kind: 'research', chatId: featureId, mode: 'research', chatType: 'research' };
  }
  return { kind: 'none' };
}

/** Pick an icon for a notification row from its feature key. */
export function featureToIcon(feature?: string): LucideIcon {
  if (feature && DRAFT_FEATURES.has(feature)) return FileEdit;
  if (feature && RESEARCH_FEATURES.has(feature)) return Search;
  if (feature?.startsWith('doc_')) return FileText;
  return Bell;
}
